import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { UserPlus, Pencil, Trash2, Power } from "lucide-react";

const PERMISSION_GROUPS = [
  { title: "Intake", perms: [
    ["view_incoming", "View Incoming Packages"],
    ["scan_intake", "Scan Package Intake"],
    ["confirm_received", "Confirm Package Received"],
    ["report_issue", "Report Package Issue"],
  ]},
  { title: "Sorting", perms: [
    ["view_received", "View Received Packages"],
    ["mark_sorting", "Mark Package for Sorting"],
    ["add_to_batch", "Add Package to Batch"],
    ["remove_from_batch", "Remove Package from Batch"],
  ]},
  { title: "Outbound", perms: [
    ["view_batches", "View Outbound Batches"],
    ["create_batch", "Create Outbound Batch"],
    ["edit_batch", "Edit Outbound Batch"],
    ["mark_batch_ready", "Mark Batch Ready for Dispatch"],
    ["mark_batch_shipped", "Mark Batch Shipped Out"],
    ["mark_last_mile", "Mark Ready for Last-Mile Pickup"],
    ["print_manifest", "Print Batch Manifest"],
  ]},
  { title: "History", perms: [
    ["view_history", "View Warehouse History"],
  ]},
];

const DEFAULT_PERMS = [
  "view_incoming", "scan_intake", "confirm_received",
  "view_received", "mark_sorting", "add_to_batch",
  "view_batches", "print_manifest", "view_history",
];

// Quick-perm presets — one-tap role bundles that match how warehouse teams
// are usually divided. "Apply" replaces the current selection.
const PRESETS = [
  {
    key: "receiving",
    label: "Receiving Only",
    description: "Intake desk · scan inbound & confirm received",
    perms: ["view_incoming", "scan_intake", "confirm_received", "report_issue"],
  },
  {
    key: "sorting",
    label: "Sorting Only",
    description: "Floor sorter · prep packages for outbound batches",
    perms: [
      "view_received", "mark_sorting", "add_to_batch", "remove_from_batch",
      "view_batches", "report_issue",
    ],
  },
  {
    key: "outbound",
    label: "Outbound Captain",
    description: "Owns batch lifecycle from creation to dispatch",
    perms: [
      "view_received", "add_to_batch", "remove_from_batch",
      "view_batches", "create_batch", "edit_batch",
      "mark_batch_ready", "mark_batch_shipped", "mark_last_mile",
      "print_manifest", "view_history",
    ],
  },
  {
    key: "full",
    label: "Full Access",
    description: "Every action — admin-equivalent for warehouse ops",
    perms: [
      "view_incoming", "scan_intake", "confirm_received", "report_issue",
      "view_received", "mark_sorting", "add_to_batch", "remove_from_batch",
      "view_batches", "create_batch", "edit_batch",
      "mark_batch_ready", "mark_batch_shipped", "mark_last_mile",
      "print_manifest", "view_history",
    ],
  },
];

export default function Dispatchers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null=closed, {}=new, {...}=edit
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/warehouse/dispatchers");
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({
    _new: true, full_name: "", email: "", phone: "", password: "", confirm_password: "",
    status: "ACTIVE", permissions: DEFAULT_PERMS,
  });
  const startEdit = (d) => setEditing({ ...d, password: "", confirm_password: "", permissions: d.permissions || DEFAULT_PERMS });

  const togglePerm = (key) => {
    const perms = new Set(editing.permissions || []);
    perms.has(key) ? perms.delete(key) : perms.add(key);
    setEditing({ ...editing, permissions: Array.from(perms) });
  };

  const save = async () => {
    if (!editing.full_name?.trim() || !editing.email?.trim()) {
      toast.error("Name and email required"); return;
    }
    if (editing._new) {
      if (!editing.password) { toast.error("Password required"); return; }
      if (editing.password !== editing.confirm_password) { toast.error("Passwords do not match"); return; }
    } else if (editing.password && editing.password !== editing.confirm_password) {
      toast.error("Passwords do not match"); return;
    }
    setSaving(true);
    try {
      if (editing._new) {
        await api.post("/warehouse/dispatchers", {
          full_name: editing.full_name, email: editing.email, phone: editing.phone,
          password: editing.password, confirm_password: editing.confirm_password,
          status: editing.status, permissions: editing.permissions,
        });
        toast.success("Dispatcher created");
      } else {
        const body = {
          full_name: editing.full_name, email: editing.email, phone: editing.phone,
          status: editing.status, permissions: editing.permissions,
        };
        if (editing.password) body.password = editing.password;
        await api.put(`/warehouse/dispatchers/${editing.id}`, body);
        toast.success("Dispatcher updated");
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const toggleStatus = async (d) => {
    try {
      await api.post(`/warehouse/dispatchers/${d.id}/${d.status === "ACTIVE" ? "deactivate" : "activate"}`);
      toast.success(d.status === "ACTIVE" ? "Deactivated" : "Activated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async () => {
    try {
      await api.delete(`/warehouse/dispatchers/${confirmDelete.id}`);
      toast.success("Deleted");
      setConfirmDelete(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="wh-dispatchers-page" className="space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-cyan-400 mb-1">/ Team</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Warehouse Dispatchers</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm">Manage staff who help with intake, sorting, and outbound batches.</p>
        </div>
        <Button data-testid="wh-disp-add" onClick={startNew} className="bg-cyan-400 text-slate-950 hover:bg-cyan-300">
          <UserPlus className="w-4 h-4 mr-2" /> Add Dispatcher
        </Button>
      </div>

      {loading ? (
        <div className="py-14 text-center text-slate-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-slate-200 dark:border-slate-800 rounded-lg py-14 text-center bg-slate-100/30 dark:bg-slate-900/30 text-slate-500">
          No dispatchers yet. Add your first above.
        </div>
      ) : (
        <div className="border border-slate-200 dark:border-slate-800 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-100/60 dark:bg-slate-900/60 text-[11px] font-mono-tech uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Phone</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Added</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {rows.map(d => (
                <tr key={d.id} data-testid={`wh-disp-row-${d.id}`} className="hover:bg-slate-100/40 hover:dark:bg-slate-900/40">
                  <td className="px-4 py-3 font-medium">{d.full_name}</td>
                  <td className="px-4 py-3 text-slate-700 dark:text-slate-300">{d.email}</td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{d.phone || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${d.status === "ACTIVE" ? "bg-green-500/10 text-green-400 border-green-500/30" : "bg-slate-700/40 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-600"}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-current" />{d.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-400 text-xs">{new Date(d.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-right space-x-1">
                    <Button data-testid={`wh-disp-toggle-${d.id}`} size="sm" variant="outline" onClick={() => toggleStatus(d)} className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-200 hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white">
                      <Power className="w-3.5 h-3.5" />
                    </Button>
                    <Button data-testid={`wh-disp-edit-${d.id}`} size="sm" variant="outline" onClick={() => startEdit(d)} className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-200 hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white">
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button data-testid={`wh-disp-delete-${d.id}`} size="sm" variant="outline" onClick={() => setConfirmDelete(d)} className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-red-400 hover:bg-red-500/10 hover:text-red-300">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-thin">
          <DialogHeader>
            <DialogTitle>{editing?._new ? "Add Warehouse Dispatcher" : "Edit Dispatcher"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Full Name *">
                  <Input data-testid="wh-disp-name" value={editing.full_name} onChange={(e) => setEditing({ ...editing, full_name: e.target.value })} className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white" />
                </Field>
                <Field label="Email *">
                  <Input data-testid="wh-disp-email" type="email" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white" />
                </Field>
                <Field label="Phone">
                  <Input data-testid="wh-disp-phone" value={editing.phone || ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white" />
                </Field>
                <Field label="Status">
                  <select
                    data-testid="wh-disp-status"
                    value={editing.status}
                    onChange={(e) => setEditing({ ...editing, status: e.target.value })}
                    className="w-full h-9 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md px-3 text-sm"
                  >
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                  </select>
                </Field>
                <Field label={editing._new ? "Password *" : "New Password (leave blank to keep)"}>
                  <Input data-testid="wh-disp-pass" type="password" value={editing.password || ""} onChange={(e) => setEditing({ ...editing, password: e.target.value })} className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white" />
                </Field>
                <Field label="Confirm Password">
                  <Input data-testid="wh-disp-pass2" type="password" value={editing.confirm_password || ""} onChange={(e) => setEditing({ ...editing, confirm_password: e.target.value })} className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white" />
                </Field>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] font-mono-tech uppercase tracking-[0.2em] text-slate-600 dark:text-slate-400">
                    Quick presets
                  </div>
                  {(editing.permissions?.length || 0) > 0 && (
                    <button
                      type="button"
                      data-testid="wh-disp-preset-clear"
                      onClick={() => setEditing({ ...editing, permissions: [] })}
                      className="text-[10px] font-mono-tech uppercase tracking-wider text-slate-500 hover:text-red-500 transition-colors"
                    >
                      Clear all
                    </button>
                  )}
                </div>
                <div className="grid sm:grid-cols-2 gap-2 mb-4">
                  {PRESETS.map((p) => {
                    const setEd = new Set(editing.permissions || []);
                    const setP = new Set(p.perms);
                    const active = setP.size === setEd.size &&
                      [...setP].every((x) => setEd.has(x));
                    return (
                      <button
                        key={p.key}
                        type="button"
                        data-testid={`wh-disp-preset-${p.key}`}
                        onClick={() => setEditing({ ...editing, permissions: [...p.perms] })}
                        className={`text-left rounded-md border px-3 py-2 transition-colors ${
                          active
                            ? "border-cyan-500/60 bg-cyan-500/10 text-cyan-700 dark:text-cyan-300"
                            : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 hover:border-cyan-400/50 hover:bg-cyan-500/5"
                        }`}
                      >
                        <div className="text-xs font-semibold flex items-center justify-between">
                          {p.label}
                          {active && (
                            <span className="text-[9px] font-mono-tech uppercase tracking-wider px-1.5 py-0.5 rounded bg-cyan-500/15 border border-cyan-500/40">
                              Active
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                          {p.description}
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="text-[10px] font-mono-tech uppercase tracking-[0.2em] text-slate-600 dark:text-slate-400 mb-2">Permissions</div>
                <div className="grid sm:grid-cols-2 gap-3">
                  {PERMISSION_GROUPS.map(g => (
                    <div key={g.title} className="border border-slate-200 dark:border-slate-800 rounded-md p-3 bg-slate-100/40 dark:bg-slate-900/40">
                      <div className="text-xs font-semibold mb-2">{g.title}</div>
                      <div className="space-y-1.5">
                        {g.perms.map(([key, label]) => (
                          <label key={key} className="flex items-center gap-2 text-xs cursor-pointer">
                            <Checkbox
                              data-testid={`wh-disp-perm-${key}`}
                              checked={(editing.permissions || []).includes(key)}
                              onCheckedChange={() => togglePerm(key)}
                              className="border-slate-300 dark:border-slate-600"
                            />
                            <span>{label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white">Cancel</Button>
            <Button data-testid="wh-disp-save" onClick={save} disabled={saving} className="bg-cyan-400 text-slate-950 hover:bg-cyan-300">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white">
          <DialogHeader>
            <DialogTitle>Delete dispatcher</DialogTitle>
          </DialogHeader>
          <p className="text-slate-700 dark:text-slate-300 text-sm">
            Are you sure you want to delete <b>{confirmDelete?.full_name}</b>? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)} className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white">Cancel</Button>
            <Button data-testid="wh-disp-confirm-delete" onClick={remove} className="bg-red-500 text-slate-900 dark:text-white hover:bg-red-400">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-600 dark:text-slate-400">{label}</Label>
      {children}
    </div>
  );
}
